import { getCurrencySymbol } from "@features/helpers";
import { IPaymentPlan, PeriodType } from "@features/shared/entities/payment";
import { Language } from "i18n";

type TranslateFunction = (key: string, fallback?: string) => string;

export const CHALLENGE_100K_V9_PAYWALL_STEP = 29;
export const CHALLENGE_100K_V9_DISCOUNT_PAYWALL_STEP = 30;

export const challenge100kV9Ids = [
    "d26e7c5f-91ef-4465-b3c4-69ab970fa2a7",
    "bb5f2e91-72ab-46cb-8379-4287b23f33dd",
    "bd4554ca-93e2-44aa-aa5c-2d8dc217274a"
];

export const challenge100kV9WithDiscountIds = [
    "36b9ef1f-2fea-451a-bec5-4954d03348f8",
    "56ffef77-e3ac-40f6-b8ec-14955e67e672",
    "47bd70e7-d27a-47b5-8ff4-669cd84d275b"
];

export const formatAgreementNotice = (
    buttonText: string,
    currency: string,
    priceBefore: number,
    priceAfter: number,
    trialPeriod: string,
    period: string,
    t?: TranslateFunction,
    lang?: Language
) => {
    const currencySymbol = getCurrencySymbol(currency, lang);
    const formatPriceBefore = (priceBefore / 100).toFixed(2);
    const formatPriceAfter = (priceAfter / 100).toFixed(2);

    if (t) {
        const translatedTrialPeriod = t(`agreementNotice.periods.${trialPeriod}`, trialPeriod);
        const translatedPeriod = t(`agreementNotice.periods.${period}`, period);
        const template = t("agreementNotice.template");

        return template
            .replace(/{buttonText}/g, buttonText)
            .replace(/{currencySymbol}/g, currencySymbol)
            .replace(/{priceBefore}/g, formatPriceBefore)
            .replace(/{priceAfter}/g, formatPriceAfter)
            .replace(/{trialPeriod}/g, translatedTrialPeriod)
            .replace(/{period}/g, translatedPeriod);
    }

    // Fallback to English if no translation function provided
    return `By clicking ${buttonText}, you agree to pay ${currencySymbol}${formatPriceBefore} for the first ${trialPeriod} period and authorize future charges, including automatic renewals at ${currencySymbol}${formatPriceAfter} every ${period} and additional purchases made through your account. You can cancel anytime in your account settings.`;
};

export const getPaymentInfo100kV9 = (
    id: string,
    currency: string,
    priceBefore: number,
    priceAfter: number,
    buttonText: string,
    t?: TranslateFunction,
    lang?: Language
) => {
    switch (id) {
        case "d26e7c5f-91ef-4465-b3c4-69ab970fa2a7":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "7-day", "1 month", t, lang);
        case "bb5f2e91-72ab-46cb-8379-4287b23f33dd":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "1-month", "1 month", t, lang);
        case "bd4554ca-93e2-44aa-aa5c-2d8dc217274a":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "3-month", "3 months", t, lang);
        case "36b9ef1f-2fea-451a-bec5-4954d03348f8":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "7-day", "1 month", t, lang);
        case "56ffef77-e3ac-40f6-b8ec-14955e67e672":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "1-month", "1 month", t, lang);
        case "47bd70e7-d27a-47b5-8ff4-669cd84d275b":
            return formatAgreementNotice(buttonText, currency, priceBefore, priceAfter, "3-month", "3 months", t, lang);
    }
};

export const paywall100kV9Achievements = [
    {
        title: "Lessons from the top 1% of AI experts —",
        description: " instant access to courses",
        image: "/images/onboarding/webp/paywall/achi-1.webp"
    },
    {
        title: "15+ AI tools in one place —",
        description: " ChatGPT, Gemini, Runway & more",
        image: "/images/onboarding/webp/paywall/achi-2.webp"
    },
    {
        title: "Personal growth plan —",
        description: "  built by AI pros for your goals and situation",
        image: "/images/onboarding/webp/paywall/achi-3.webp"
    },
    {
        title: "Step-by-step guides  —",
        description: "  quickly build a strong freelancer profile",
        image: "/images/onboarding/webp/paywall/achi-4.webp"
    }
];

export const paywall100kV9faq = [
    {
        question: "What's next after I make a payment?",
        answer: "Once your payment is complete, you'll get instant platform access. We'll give you your personalized plan, then unlock video lessons, AI tools, templates, and client-finding strategies — everything to start earning fast."
    },
    {
        question: "How can I cancel my subscription?",
        answer: "Go to the Subscription section of your account settings and cancel there. Cancel before the end of your current billing period to avoid the next payment."
    },
    {
        question: "Why is Tixu.ai better than others?",
        answer: "We don't give you random tips — we provide a proven, step-by-step system, real access to 15+ AI tools, and a clear path to earning. You'll work on real projects and get guided to your first clients. "
    }
];

export const paywall100kV9Feedbacks = [
    {
        author: "Daniel R",
        title: "Beyond Expectations",
        comment:
            "This lesson shows you what to expect on your freelancing journey and how to prepare for it — laying the foundation for future success.",
        date: "Experience date: Jul 28, 2025"
    },
    {
        author: "Michael T",
        title: "Why freelancing works",
        comment:
            "Perfect for anyone wanting extra income but unsure where to start. Step-by-step lessons guide you from zero to success, with tips for both beginners and experienced freelancers. They make the process easy and stress-free — highly recommended for anyone wanting to work online.",
        date: "Experience date: Aug 6, 2025"
    },
    {
        author: "Jonathan M",
        title: "A truly impressive business",
        comment:
            "This is a well-run company with valuable, well-structured course content. The lessons are packed with proven business ideas and actionable steps. If more people saw these guidelines, I'm sure many would want to start freelancing.",
        date: "Experience date: Jun 16, 2025"
    }
];

export const screenShotes100kV9 = [
    "/images/onboarding/webp/paywall/slide-v9-1.webp",
    "/images/onboarding/webp/paywall/slide-v9-2.webp",
    "/images/onboarding/webp/paywall/slide-v9-3.webp"
];

const formatPlural = (count: number, singular: string, plural: string) => {
    return count === 1 ? singular : plural;
};

export const formatTrialPeriodLabel = (trial: IPaymentPlan["trial"]) => {
    const type = (trial.TrialPeriodType || "D").toUpperCase();
    if (type === "W") return `${trial.TrialPeriod}-week`;
    if (type === "M") return `${trial.TrialPeriod}-month`;
    if (type === "Q") return `${trial.TrialPeriod * 3}-month`;
    return `${trial.TrialPeriod}-day`;
};

export const formatPeriodLabel = (period: number, periodType?: PeriodType) => {
    const type = (periodType || "M").toUpperCase();
    switch (type) {
        case "D":
            return `${period} ${formatPlural(period, "day", "days")}`;
        case "W":
            return `${period} ${formatPlural(period, "week", "weeks")}`;
        case "M":
            return `${period} ${formatPlural(period, "month", "months")}`;
        case "Q": {
            const months = period * 3;
            return `${months} months`;
        }
        default:
            return `${period} ${formatPlural(period, "month", "months")}`;
    }
};

